<script setup lang="ts">
import {computed, inject, watch} from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import DieRank from "@/types/die-rank";
import type { SwadeCharacterInterface } from "@/types/characters";
import type {IntlService} from "@progress/kendo-vue-intl";

const props = defineProps<{
    stepIndex: number;
}>();

const character = defineModel<SwadeCharacterInterface | null>('character');
const kendo: IntlService = inject('kendo') as IntlService

const ux = useUxData();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();

const attributes = ['agility', 'smarts', 'spirit', 'strength', 'vigor'] as const;

const attributeLabels = computed(() => ({
    agility: ux.labels?.swade?.attributes?.agility ?? 'Agility',
    smarts: ux.labels?.swade?.attributes?.smarts ?? 'Smarts',
    spirit: ux.labels?.swade?.attributes?.spirit ?? 'Spirit',
    strength: ux.labels?.swade?.attributes?.strength ?? 'Strength',
    vigor: ux.labels?.swade?.attributes?.vigor ?? 'Vigor',
}));

const totalPoints = computed(() => characterData.build?.attributes?.total ?? 5);
const allocatedPoints = computed(() => characterData.build?.attributes?.allocated ?? 0);
const remainingPoints = computed(() => totalPoints.value - allocatedPoints.value);

const isValid = computed(() => {
    return remainingPoints.value === 0;
});

watch(isValid, (valid) => {
    setupStore.validateStep(props.stepIndex, valid);
}, { immediate: true });

const getAttributeValue = (attr: typeof attributes[number]): string => {
    if (!character.value) return 'd4';
    return (character.value as any)[attr] ?? 'd4';
};

const setAttributeValue = (attr: typeof attributes[number], value: string) => {
    if (!character.value) return;
    (character.value as any)[attr] = value;
    onAttributeChange();
};

const getMinDie = (attr: typeof attributes[number]): string => {
    if (!character.value?.protoData?.attributes) return 'd4';
    return (character.value.protoData.attributes as any)[attr]?.base ?? 'd4';
};

const getFilteredSteps = (attr: typeof attributes[number]) => {
    const minDie = getMinDie(attr);
    const minRank = new DieRank(minDie);

    return characterData.attributeSteps.filter((step) => {
        const stepRank = new DieRank(step.value);
        return stepRank.getValue() >= minRank.getValue();
    });
};

const onAttributeChange = () => {
    if (!character.value) return;

    characterData.updateProtoAttributes(character.value);
    characterData.updateAllocatedAttributePoints(character.value);
    characterData.recalculateDerivedStats(character.value);
};
</script>

<template>
    <div class="character-attributes-step">
        <div class="form-group mb-3">
            <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.attributesLabel"></label>
          <div class="d-flex flex-fill gap-2 mb-2">
            <span class="icon mb-auto">
                <span class="fa-light fa-info-circle"></span>
            </span>
            <p class="form-text my-auto" v-html="ux.labels?.guidedSetup?.character?.attributesHelp"></p>
          </div>

            <div class="points-tracker alert mb-3" :class="remainingPoints === 0 ? 'alert-success' : remainingPoints < 0 ? 'alert-danger' : 'alert-info'">
                <div class="d-flex justify-content-between align-items-center">
                    <span>
                        <span class="fa-light fa-dice me-2"></span>
                        <span v-html="ux.labels?.guidedSetup?.character?.attributePoints"></span>
                    </span>
                    <span class="badge fs-6" :class="remainingPoints === 0 ? 'bg-success' : remainingPoints < 0 ? 'bg-danger' : 'bg-primary'">
                        {{ allocatedPoints }} / {{ totalPoints }}
                    </span>
                </div>
                <small v-if="remainingPoints > 0"
                       class="d-block mt-1"
                       v-html="kendo.format(ux.labels?.guidedSetup?.character?.pointsRemainingFormatString, remainingPoints)"></small>
                <small v-else-if="remainingPoints < 0"
                       class="d-block mt-1 text-danger"
                       v-html="kendo.format(ux.labels?.guidedSetup?.character?.pointsOverFormatString, Math.abs(remainingPoints))"></small>
            </div>
            <div class="attribute-list">
                <div v-for="attr in attributes" :key="attr" class="attribute-row d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                    <label class="attribute-name fw-semibold mb-0">{{ attributeLabels[attr] }}</label>
                    <select
                        :value="getAttributeValue(attr)"
                        class="form-select w-auto"
                        @change="setAttributeValue(attr, ($event.target as HTMLSelectElement).value)"
                    >
                        <option
                            v-for="step in getFilteredSteps(attr)"
                            :key="step.value"
                            :value="step.value"
                        >
                            {{ step.title }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>
